import { useEffect, useState, type ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Contexts
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Components
import { IndexChartsArea } from '../index-charts-area/index-charts-area';
import { PalmaresTable } from '../palmares-table/palmares-table';
// Types
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import RunnerStatisticView from '@utmb/types/view/RunnerStatisticView';
// Utils
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
import { confirmAlert } from 'react-confirm-alert';
// Styles
import 'react-confirm-alert/src/react-confirm-alert.css';
import styles from './index-statistics-palmares-area.module.scss';

export interface IndexStatisticsPalmaresAreaProps {
    isMe?: boolean;
    results: RaceYearResultForRunnerView[] | null;
    runnerStatistic?: RunnerStatisticView | null;
    profilePicture?: string | null;
    fullname?: string;
}

export function IndexStatisticsPalmaresArea({
    isMe = false,
    results,
    runnerStatistic = null,
    profilePicture = null,
    fullname,
}: IndexStatisticsPalmaresAreaProps): ReactElement {
    const [allResults, setAllResults] = useState<RaceYearResultForRunnerView[] | null>(results);

    useEffect(() => {
        setAllResults(results);
    }, [results]);

    const { t } = useTranslation();
    const { getToken, isAuthenticated } = useUser();
    const { tenantId } = useTenancy();

    const handleDeleteResult = (uri: string): void => {
        confirmAlert({
            title: t('singleRunner.removeResult.title'),
            message: t('singleRunner.removeResult.message'),
            closeOnClickOutside: true,
            closeOnEscape: true,
            buttons: [
                {
                    label: t('common.confirm'),
                    onClick: async () => {
                        const token = await getToken();
                        await fetchTheAPI({
                            tenantId,
                            path: `/users/me/detach-result/${uri}`,
                            bearer: token,
                            method: FetchMethodsEnum.POST,
                        });
                        setAllResults((prev) => prev?.filter((r) => r.uri !== uri) ?? null);
                    },
                },
                { label: t('common.cancel'), onClick: () => null },
            ],
        });
    };

    return (
        <div className={styles.indexStatisticsPalmaresArea}>
            <IndexChartsArea
                results={allResults ?? []}
                runnerStatistic={runnerStatistic}
                profilePicture={profilePicture}
                fullname={fullname}
            />
            <PalmaresTable results={allResults ?? []} isAuthenticated={!!isAuthenticated} isMe={isMe} onDeleteResult={handleDeleteResult} />
        </div>
    );
}
